import { c as _c } from "react/compiler-runtime";
import { Tab, TabGroup, TabList } from '@headlessui/react';
import { FormInput, NeutralButton, NeutralTypeEnum, PrimaryButton } from '@systemeio/ui';
import { ContactFilterSavedInterface, ContactFilterType, ContactFiltererInterface } from 'modules/contacts/types/contact-filter-interface';
import { getDefaultFilterValue } from 'modules/contacts/utils/get-default-filter-value';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ConfirmationModal from 'shared/components/confirmation-modal';
import CreateModal from 'shared/components/create-modal';
import { FilterableItemType, ListingLayoutProps } from 'shared/components/listing-layout';
import { Loader } from 'shared/components/loader';
import { BaseTableProps } from 'shared/components/table/components/base-table';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import { DataWithPaginationInterface } from 'shared/types/pagination-interface';
import { getFullOffsetOfElement } from 'shared/utils/get-full-offset-of-element';
import { CloseIcon, FilterIcon } from '@systemeio/ui/icons';
import { BadRequest } from '@systemeio/util';
const defaultSaveFilterValues = {
  name: ''
};
interface ContactListingLayoutProps<T extends DataWithPaginationInterface<T['items'][0]>, K, R> extends Pick<ListingLayoutProps<T, K, R>, 'data' | 'title' | 'children' | 'customHeaderElement' | 'counter'> {
  isMenuShown: boolean;
  filterData: {
    filterable: FilterableItemType<ContactFilterType>[];
    applySavedFilters: (savedFilters: ContactFilterSavedInterface) => void;
    onDeleteSavedFilter: (filter: ContactFilterSavedInterface) => Promise<void>;
    onSaveNewFilter: (data: {
      name: string;
    }) => Promise<void>;
    onResetFilters: () => void;
    filterer: ContactFiltererInterface;
  } & Required<Pick<BaseTableProps<T>, 'paginator' | 'limiter'>>;
}
const DEFAULT_INDENT = 20;
const MARGIN_BOTTOM_XL = 60;
const MARGIN_BOTTOM = 90;
const FILTER_INSIDE_PADDING = 32;
const ContactsListingLayout = t0 => {
  const $ = _c(79);
  const {
    title,
    data,
    counter,
    filterData,
    children,
    customHeaderElement,
    isMenuShown
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    isXL
  } = useMediaQuery();
  const tableRef = useRef(null);
  const filterRef = useRef(null);
  const filterContainerRef = useRef(null);
  const [isFilterShown, setIsFilterShown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [savedFilters, setSavedFilters] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [deleteModalOpened, setDeleteModalOpened] = useState(t2);
  const [saveModalOpened, setSaveModalOpened] = useState(false);
  const [errorSaveFilter, setErrorSaveFilter] = useState();
  let t3;
  let t4;
  if ($[2] !== filterData.filterer.savedFilters) {
    t3 = () => {
      if (filterData.filterer.savedFilters) {
        setSavedFilters(filterData.filterer.savedFilters);
        setDeleteModalOpened(filterData.filterer.savedFilters.reduce(_temp, {}));
      }
    };
    t4 = [filterData.filterer.savedFilters];
    $[2] = filterData.filterer.savedFilters;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== data || $[6] !== isMenuShown || $[7] !== isXL) {
    t5 = () => {
      const paginationHeight = data ? 54 : 0;
      const html = document.querySelector("html");
      const resizeFilter = () => {
        if (filterRef.current && tableRef.current && filterContainerRef.current) {
          const htmlScrollTop = html?.scrollTop || 0;
          const windowInnerHeight = window.innerHeight;
          const fullScreenScrollHeight = htmlScrollTop + windowInnerHeight;
          const defaultFilterOffset = filterContainerRef.current.offsetHeight - filterRef.current.offsetHeight - DEFAULT_INDENT - FILTER_INSIDE_PADDING;
          const tableOffset = getFullOffsetOfElement(tableRef.current);
          const filterFullOffset = getFullOffsetOfElement(filterRef.current) + defaultFilterOffset;
          const offsetFromBottom = Math.max(fullScreenScrollHeight + paginationHeight - tableRef.current.scrollHeight - tableOffset - DEFAULT_INDENT, 0);
          if (isXL) {
            if (tableRef.current.clientHeight < windowInnerHeight - tableOffset - DEFAULT_INDENT) {
              filterRef.current.style.maxHeight = `${fullScreenScrollHeight - filterFullOffset - (isMenuShown ? MARGIN_BOTTOM_XL : 0)}px`;
            } else {
              filterRef.current.style.maxHeight = `${fullScreenScrollHeight - filterFullOffset - offsetFromBottom}px`;
            }
          } else {
            filterRef.current.style.maxHeight = "initial";
          }
        }
      };
      resizeFilter();
      window.addEventListener("scroll", resizeFilter);
      window.addEventListener("resize", resizeFilter);
      return () => {
        window.removeEventListener("scroll", resizeFilter);
        window.removeEventListener("resize", resizeFilter);
      };
    };
    t6 = [data, isMenuShown, isXL];
    $[5] = data;
    $[6] = isMenuShown;
    $[7] = isXL;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useLayoutEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== filterData) {
    t7 = () => {
      if (filterData) {
        const noRenderArr = filterData.filterable.filter(_temp2).map(_temp3);
        filterData.filterer.setFilter(prev => {
          noRenderArr.forEach(el_2 => {
            prev[el_2] = getDefaultFilterValue(el_2 as string);
          });
          return prev;
        });
      }
    };
    t8 = [filterData];
    $[10] = filterData;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== filterData || $[14] !== savedFilters) {
    t9 = async filter => {
      await filterData.onDeleteSavedFilter(filter);
      if (savedFilters.length === 0) {
        setSelectedIndex(0);
      }
    };
    $[13] = filterData;
    $[14] = savedFilters;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const onDeleteSavedFilter = t9;
  let t10;
  if ($[16] !== counter || $[17] !== data?.items.length || $[18] !== title) {
    t10 = data?.items.length === 0 || counter === false ? <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1> : <div className="flex items-center gap-1">{counter ? <h2 className="text-text-primary whitespace-nowrap text-[22px] font-bold">{counter.count}</h2> : <Loader type="dark" small={true} />}<h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1></div>;
    $[16] = counter;
    $[17] = data?.items.length;
    $[18] = title;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== filterData || $[21] !== isFilterShown) {
    t11 = filterData && <PrimaryButton onClick={() => setIsFilterShown(!isFilterShown)} className="flex items-center xl:hidden"><FilterIcon className="h-[15px] w-[15px]" /></PrimaryButton>;
    $[20] = filterData;
    $[21] = isFilterShown;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== customHeaderElement) {
    t12 = customHeaderElement && <div>{customHeaderElement}</div>;
    $[23] = customHeaderElement;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = <div className="flex gap-3">{t11}{t12}</div>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t13) {
    t14 = <div className="mb-5 flex justify-between">{t10}{t13}</div>;
    $[28] = t10;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = `flex ${isFilterShown ? "gap-5" : "gap-0 xl:gap-5"} flex-col xl:flex-row`;
  const t16 = `bg-brand-primary-subtle justify-center ${isFilterShown ? "flex" : "hidden xl:flex"}`;
  let t17;
  if ($[31] !== t) {
    t17 = t("global.filter_by");
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17) {
    t18 = <Tab className={_temp4}><span className="font-inter whitespace-nowrap text-[17px] font-bold">{t17}</span></Tab>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = !filterData.filterer.savedFilters || filterData.filterer.savedFilters.length === 0;
  let t20;
  if ($[35] !== t) {
    t20 = t("dashboard.contacts.filter.saved_filters");
    $[35] = t;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t20) {
    t21 = <span className="font-inter whitespace-nowrap text-[17px] font-bold">{t20}</span>;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t19 || $[40] !== t21) {
    t22 = <Tab disabled={t19} className={_temp5}>{t21}</Tab>;
    $[39] = t19;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t18 || $[43] !== t22) {
    t23 = <TabList className="flex w-full">{t18}{t22}</TabList>;
    $[42] = t18;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== selectedIndex || $[46] !== t23) {
    t24 = <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>{t23}</TabGroup>;
    $[45] = selectedIndex;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t16 || $[49] !== t24) {
    t25 = <div className={t16}>{t24}</div>;
    $[48] = t16;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  const t26 = `bg-background-secondary flex w-full flex-col flex-wrap justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row xl:flex-col xl:px-2 ${isFilterShown ? "flex" : "hidden xl:flex"}`;
  let t27;
  if ($[51] !== deleteModalOpened || $[52] !== errorSaveFilter || $[53] !== filterData || $[54] !== onDeleteSavedFilter || $[55] !== saveModalOpened || $[56] !== savedFilters || $[57] !== selectedIndex || $[58] !== t) {
    t27 = selectedIndex === 0 ? <><div ref={filterRef} className="customScroll flex w-full flex-col gap-3 xl:overflow-y-auto">{filterData.filterable.map(el_3 => el_3.shouldRender !== false && el_3.onRender(filterData.filterer.filter[el_3.field] ?? [], value => {
          filterData.filterer.setFilter(prev_0 => ({
            ...prev_0,
            [el_3.field]: value
          }));
        }, filterData.filterer.filter, () => filterData.filterer.setFilter(_temp6)))}</div><div className="mt-3 flex w-full flex-col gap-3"><div className="flex gap-1.5"><NeutralButton type="button" disabled={!filterData.filterer.filterFields} onClick={e => {
            e.preventDefault();
            setSaveModalOpened(true);
          }} className="group w-full"><span className="main-transition-colors group-hover:text-brand-primary">{t("dashboard.contacts.filter.save_filter")}</span></NeutralButton><CreateModal needToast={false} fullCaption={t("dashboard.contacts.filter.save_new_filter")} creatable={[{
            field: "name",
            onRender: (value_0, onChange) => <FormInput label={t("dashboard.contacts.filter.filter_name")} key="create-name" value={value_0} onChange={e_0 => onChange(e_0.target.value)} error={errorSaveFilter} />
          }]} opened={saveModalOpened} onClose={() => setSaveModalOpened(false)} afterLeave={() => setErrorSaveFilter(undefined)} defaultValues={defaultSaveFilterValues} onCreate={async data_0 => {
            setErrorSaveFilter(undefined);
            await filterData.onSaveNewFilter(data_0).catch(e_1 => {
              if (e_1 instanceof BadRequest) {
                setErrorSaveFilter(e_1.errors.fields.name);
              }
              throw e_1;
            });
            setSelectedIndex(1);
          }} requiredFields={["name"]} /><NeutralButton type="reset" disabled={!filterData.filterer.filterFields} onClick={e_2 => {
            e_2.preventDefault();
            filterData.onResetFilters();
          }} neutralType={NeutralTypeEnum.danger} className="group w-full"><span className="main-transition-colors group-hover:text-danger">{t("global.reset")}</span></NeutralButton></div></div></> : <>{savedFilters.map((el_4, idx) => <div key={el_4.id} className={`flex w-full items-center justify-between px-4 ${idx !== savedFilters.length - 1 && "border-b-text-secondary/30 border-b pb-3"}`}><button onClick={e_3 => {
          e_3.preventDefault();
          setSelectedIndex(0);
          filterData.applySavedFilters(el_4);
        }} className="primary-link">{el_4.name}</button><button onClick={e_4 => {
          e_4.preventDefault();
          setDeleteModalOpened(prev_2 => ({
            ...prev_2,
            [el_4.name]: true
          }));
        }} className="group outline-hidden"><CloseIcon className="fill-text-tertiary hover:fill-text-primary group-focus-visible:fill-text-primary" /></button><ConfirmationModal confirmationText={t("dashboard.contacts.filter.delete_filter_confirmation", {
          filter_name: el_4.name
        })} opened={deleteModalOpened[el_4.name] ?? false} onClose={() => setDeleteModalOpened(prev_3 => ({
          ...prev_3,
          [el_4.name]: false
        }))} onConfirm={() => onDeleteSavedFilter(el_4)} /></div>)}</>;
    $[51] = deleteModalOpened;
    $[52] = errorSaveFilter;
    $[53] = filterData;
    $[54] = onDeleteSavedFilter;
    $[55] = saveModalOpened;
    $[56] = savedFilters;
    $[57] = selectedIndex;
    $[58] = t;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t26 || $[61] !== t27) {
    t28 = <form className={t26}>{t27}</form>;
    $[60] = t26;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t25 || $[64] !== t28) {
    t29 = <div ref={filterContainerRef} className="top-5 flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible xl:sticky xl:inset-0 xl:h-fit xl:w-fit xl:min-w-[307px] xl:max-w-[400px] xl:shrink-0 xl:overflow-x-hidden">{t25}{t28}</div>;
    $[63] = t25;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== isMenuShown || $[67] !== isXL) {
    t30 = isMenuShown ? {
      style: {
        marginBottom: isXL ? MARGIN_BOTTOM_XL : MARGIN_BOTTOM
      }
    } : {};
    $[66] = isMenuShown;
    $[67] = isXL;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== children || $[70] !== t30) {
    t31 = <div ref={tableRef} {...t30} className="h-fit w-full">{children}</div>;
    $[69] = children;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t15 || $[73] !== t29 || $[74] !== t31) {
    t32 = <div className={t15}>{t29}{t31}</div>;
    $[72] = t15;
    $[73] = t29;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t14 || $[77] !== t32) {
    t33 = <>{t14}{t32}</>;
    $[76] = t14;
    $[77] = t32;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  return t33;
};
export default ContactsListingLayout;
function _temp(obj, el) {
  return {
    ...obj,
    [el.name]: false
  };
}
function _temp2(el_0) {
  return el_0.shouldRender === false;
}
function _temp3(el_1) {
  return el_1.field;
}
function _temp4(t0) {
  const {
    selected
  } = t0;
  return `${selected ? "text-text-primary border-b-brand-primary border-b-2" : "border-b-2 border-b-transparent"} main-transition-colors w-full px-7 pb-4 pt-4 outline-hidden`;
}
function _temp5(t0) {
  const {
    selected: selected_0
  } = t0;
  return `${selected_0 ? "text-text-primary border-b-brand-primary border-b-2" : "border-b-2 border-b-transparent"} main-transition-colors disabled:text-text-tertiary/50 w-full px-7 pb-4 pt-4 outline-hidden`;
}
function _temp6(prev_1) {
  return prev_1;
}